import { Component, AfterViewInit, ViewChild, forwardRef } from '@angular/core';
import { RailroadSubCode } from 'src/shared/api';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	ControlValueAccessor,
	FormGroup,
	FormArray,
	FormBuilder,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AbstractControlDataSource } from 'src/shared/form-models/abstract-control-data-source';
import { takeUntil, filter } from 'rxjs/operators';
import { railroadSubCodeFormConfig } from 'src/shared/railroad-sub-code/railroad-sub-code.component';
import { RailroadSubCodeDialogComponent } from 'src/shared/railroad-sub-code/railroad-sub-code-dialog/railroad-sub-code-dialog.component';
import { animateList } from 'src/shared/animations';

@Component({
	selector: 'rpfsa-sub-codes-list',
	templateUrl: './sub-codes-list.component.html',
	animations: [animateList],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SubCodesListComponent),
			multi: true,
		},
	],
})
export class SubCodesListComponent extends SelfDestruct
	implements ControlValueAccessor, AfterViewInit {
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

	emptyColumns = ['empty'];
	resultColumns = ['name', 'edit', 'delete'];

	// Form Controls
	parent = new FormGroup({
		array: new FormArray([]),
	});

	dataSource = new AbstractControlDataSource<RailroadSubCode>(this.array);

	constructor(
		private readonly dialog: MatDialog,
		private readonly fb: FormBuilder
	) {
		super();
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	// model --> view
	writeValue(value: RailroadSubCode[]): void {
		if (value) {
			this.array.clear();
			value.forEach((v) => {
				const fc = this.fb.group(railroadSubCodeFormConfig) as FormGroup;
				fc.setValue(v);
				this.array.push(fc);
			});
		}
	}

	// view --> model
	registerOnChange(fn: (value: RailroadSubCode[]) => void) {
		this.array.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	onTouched: () => void = () => {};

	addSubCode() {
		this.dialog
			.open<RailroadSubCodeDialogComponent, never, FormGroup>(
				RailroadSubCodeDialogComponent,
				{ width: '450px' }
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				(this.parent.get('array') as FormArray).insert(0, control);
			});
	}

	editSubCode(subCode: RailroadSubCode) {
		this.dialog
			.open<RailroadSubCodeDialogComponent, RailroadSubCode, FormGroup>(
				RailroadSubCodeDialogComponent,
				{
					width: '450px',
					data: subCode
				}
			)
			.afterClosed()
			.pipe(filter((control) => !!control))
			.subscribe((control) => {
				(this.parent.get('array') as FormArray).setControl(
					this.findSubcodeIndex(subCode),
					control
				);
			});
	}

	findSubcodeIndex(subcode: RailroadSubCode){
		return this.dataSource.data.findIndex(s => s === subcode);
	}

	get array() {
		return this.parent.get('array') as FormArray;
	}
}
